import React from 'react';
import { Search, X } from 'lucide-react';

/**
 * Search input that owns its own local state and only notifies the parent
 * with the debounced value via `onSearch`. This prevents parent components
 * from re-rendering on every keystroke — critical for large pages.
 *
 * Props:
 *   value        {string}   - Controlled value from parent (used for external reset, e.g. "Clear all")
 *   onSearch     {Function} - Called with the debounced search string
 *   placeholder  {string}   - Input placeholder text
 *   debounceMs   {number}   - Debounce delay in ms (default: 300)
 *   className    {string}   - Additional class names for the wrapper div
 *   inputClassName {string} - Additional class names for the input element
 *   theme        {string}   - 'admin' (red/search-box-admin) or 'employee' (blue/search-box)
 */
const DebouncedSearchInput = React.memo(({
  value = '',
  onSearch,
  placeholder = 'Search...',
  debounceMs = 300,
  className = '',
  inputClassName = '',
  theme = 'admin',
}) => {
  const [localValue, setLocalValue] = React.useState(value);
  const debounceRef = React.useRef(null);

  // Sync when parent resets value externally (e.g. "Clear all", or a
  // department switch that clears the roster search). Cancel any pending
  // debounce so a keystroke from the previous scope cannot write the old
  // term back after the parent has already cleared it.
  React.useEffect(() => {
    setLocalValue(value ?? '');
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, [value]);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const handleChange = (e) => {
    const newVal = e.target.value;
    setLocalValue(newVal);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(newVal), debounceMs);
  };

  const handleClear = () => {
    setLocalValue('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onSearch('');
  };

  return (
    <div className={`relative flex-1 ${className}`}>
      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
      <input
        type="text"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        className={`${theme === 'admin' ? 'search-box-admin' : 'search-box'} w-full pr-10 ${inputClassName}`}
      />
      {localValue && (
        <button
          onClick={handleClear}
          className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${theme === 'admin' ? 'icon-button-admin icon-button-sm' : 'icon-button icon-button-sm'}`}
          title="Clear search"
          aria-label="Clear search"
          type="button"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
});

DebouncedSearchInput.displayName = 'DebouncedSearchInput';

export default DebouncedSearchInput;
